// Shared by the bulk Addition/Removal templates (ICIB-02 IntakeView,
// EMP-03 NewStarterView, EMP-04 LeaversView). Kept out of utils/xlsx.js
// on purpose — that module pulls in exceljs and is lazy-loaded, while
// these helpers are needed on every upload (CSV included).
//
// The .xlsx template no longer has a single subsidised_product_level
// column with ';'-separated names — each of the group's products gets its
// own column, headed by the product name, with a Yes/No in-cell dropdown
// so admins just mark which cover applies to that person. On upload the
// Yes columns are folded back into the subsidised_product_level id array
// the bulk endpoint expects (README §4.4 / ICIB-174).
//
// company_code is also no longer a column in the sheet, and it isn't sent
// either: the bulk endpoint takes the company from company_id in the URL,
// and a row carrying a company_code key is rejected as an unknown form
// field, failing the whole upload (README §4.4). If an older or
// hand-built file still carries the column (company_code, or group_code
// from before the company rename), it's just dropped here.
const CODE_COLUMNS = new Set(['company_code', 'group_code']);

export const YES_NO = ['Yes', 'No'];

const YES = new Set(['yes', 'y', 'true', '1']);

// Expands a column list's subsidised_product_level slot into one column
// per product name.
export function withProductColumns(columns, products) {
  return columns.flatMap((c) => (c === 'subsidised_product_level' ? products.map((p) => p.name) : [c]));
}

// listDropdowns entries for downloadXlsxTemplate — one Yes/No dropdown per
// product column.
export function productDropdowns(products) {
  return products.map((p) => ({ header: p.name, values: YES_NO }));
}

// Splits a legacy subsidised_product_level cell (a name/id, or several
// separated by ;) into ids — kept so an older template or a hand-built CSV
// that still carries that column keeps uploading the same way.
function legacyProductIds(raw, products) {
  return (raw == null ? '' : String(raw)).split(/;/).map((s) => s.trim()).filter(Boolean).map((token) => {
    const byId = products.find((p) => p.id === token);
    if (byId) return byId.id;
    const byName = products.find((p) => (p.name || '').toLowerCase() === token.toLowerCase());
    return byName ? byName.id : token;
  });
}

// Header matching ignores case and stray/doubled spaces, so a column
// that picked up a trailing space in Excel still lines up with its product.
function normalise(s) {
  return String(s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}
function productLookup(products) {
  return new Map(products.map((p) => [normalise(p.name), p]));
}

// EXTR-13 — the downloaded template heads each column with the wording
// from ICIB's own Additions/Removals spreadsheet instead of the API's
// field_key, so it reads like the sheet admins already know. Product
// columns stay headed by product name (one Yes/No column each, above),
// where the sample has a single "Subsidised Product Level" dropdown.
export const COLUMN_HEADINGS = {
  last_name: 'Surname',
  first_name: 'First Name',
  dob: 'Date of Birth',
  gender: 'Biological Sex',
  nz_citizen_or_visa: 'NZ Citizen or 2 year work Visa',
  email: 'Email address',
  address: 'Postal Address',
  phone: 'Cellphone',
  effective_date: 'Employment or Termination Effective Date',
  action: 'Addition/Removal',
  reason: 'Reason/Comments',
  // Columns only the ICIB-04 Confirmations and ICIB-08 Users templates
  // carry — the sample sheet has no equivalent, so these follow its style.
  onboarding_status: 'Onboarding Status',
  company_code: 'Company Code',
  job_title: 'Job Title',
};

// field_key -> heading for one template: the fixed headings above, plus
// each custom form_data field's own label. A custom label that would
// clash with a fixed heading or a product column is skipped (that column
// keeps its field_key), so every heading still maps back to one key.
export function columnHeadings(customFields = [], products = []) {
  const taken = new Set([...Object.values(COLUMN_HEADINGS), ...products.map((p) => p.name)].map(normalise));
  const headings = {};
  customFields.forEach((f) => {
    if (COLUMN_HEADINGS[f.field_key] || !f.label || taken.has(normalise(f.label))) return;
    taken.add(normalise(f.label));
    headings[f.field_key] = f.label;
  });
  return { ...headings, ...COLUMN_HEADINGS };
}

// Swaps field_keys for headings in the pieces downloadXlsxTemplate keys
// by column name (headers, listDropdowns, dateColumns).
export function withHeadings({ headers, listDropdowns, dateColumns }, headings) {
  const h = (key) => headings[key] || key;
  return {
    headers: headers.map(h),
    listDropdowns: (listDropdowns || []).map((d) => ({ ...d, header: h(d.header) })),
    dateColumns: (dateColumns || []).map(h),
  };
}

// The inverse, run on a parsed file before anything else reads it:
// renames each heading back to its field_key (ignoring case/spacing like
// product matching does). A column already headed by its field_key — an
// older template or a hand-built CSV — or by a product name passes
// through untouched.
export function fromHeadings(rows, headings) {
  const keyByHeading = new Map(Object.entries(headings).map(([key, heading]) => [normalise(heading), key]));
  return rows.map((row) => Object.fromEntries(
    Object.entries(row).map(([heading, value]) => [keyByHeading.get(normalise(heading)) || heading, value]),
  ));
}

// Turns one parsed row (header -> cell text) into the shape the bulk
// endpoint expects: product-name columns removed from every row (an
// UPDATE row would otherwise be rejected for carrying a non-profile
// field), ADDITION/REMOVAL rows given a subsidised_product_level id array
// built from the columns marked Yes, and any company_code/group_code column dropped.
export function toUploadRow(row, products) {
  const out = {};
  const selected = [];
  const productByName = productLookup(products);
  for (const [key, value] of Object.entries(row)) {
    const product = productByName.get(normalise(key));
    if (product) {
      if (YES.has(String(value ?? '').trim().toLowerCase())) selected.push(product.id);
    } else if (key !== 'subsidised_product_level' && !CODE_COLUMNS.has(key)) {
      out[key] = value;
    }
  }
  const action = String(row.action || '').toUpperCase();
  if (action === 'ADDITION' || action === 'REMOVAL') {
    const ids = [...selected, ...legacyProductIds(row.subsidised_product_level, products)];
    out.subsidised_product_level = [...new Set(ids)];
  }
  return out;
}

// Fields the template pre-fills, so a row holding nothing else is just the
// starter row left untouched (e.g. with the real data typed below it).
const PREFILLED = new Set(['action', 'effective_date', ...CODE_COLUMNS]);

// Prepares a whole parsed file for upload: drops untouched starter rows,
// converts the rest with toUploadRow, and flags any ADDITION/REMOVAL row
// with no product marked Yes — the API rejects that with a bare "This list
// may not be empty", so it's caught here with a clearer message instead.
// Row numbers are the spreadsheet's own (header = row 1).
export function prepareUploadRows(rows, products) {
  const productByName = productLookup(products);
  const out = [];
  const problems = [];
  rows.forEach((row, i) => {
    const untouched = Object.entries(row).every(([key, value]) => {
      const v = String(value ?? '').trim();
      if (productByName.has(normalise(key))) return v === '' || !YES.has(v.toLowerCase());
      return PREFILLED.has(key) || v === '';
    });
    if (untouched) return;
    const uploadRow = toUploadRow(row, products);
    if (Array.isArray(uploadRow.subsidised_product_level) && !uploadRow.subsidised_product_level.length) {
      problems.push(`Row ${i + 2}${row.email ? ` (${row.email})` : ''}: mark at least one product Yes.`);
    }
    out.push(uploadRow);
  });
  return { rows: out, problems };
}
